import React from 'react';
import { api } from '../api.js';
import { Attachments } from './attachments.jsx';
import { FormManager } from './approval-forms.jsx';
import { DelegationManager } from './approval-delegations.jsx';
import { mapApprovalDetail, mapApprovals } from '../mapping.js';
import { Avatar, Empty, Icon, KV, Lbl, Modal, PH, PageTabs, Panel, StatusPill, Token } from './components.jsx';
import { ME, PEOPLE } from './data.jsx';
import { Button, DataTable, Field, Overline, Pill, RadioGroup, SegmentedControl } from './ds/fgcp-ds.jsx';

// approval.jsx — 전자결재 (결재함 · 문서 상세 · 기안)
const ADT = DataTable;
const ABtn = Button;
const APill = Pill;
const AField = Field;
const AOverline = Overline;
const { useState: uA, useEffect: uAE } = React;

// 결재선 줄의 처리 이름 — 합의 줄의 승인·반려는 합의·반대(서버는 같은 상태값을 쓰고 줄 종류가 뜻을 정한다)
const DECIDED = { approve: { approved: '승인', rejected: '반려' }, agree: { approved: '합의', rejected: '반대' } };
const decidedWord = (kind, state) => DECIDED[kind === 'agree' ? 'agree' : 'approve'][state];

function ApprovalLine({ line }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, overflowX: 'auto' }}>
      {line.map((l, i) => {
        const rej = l.state === 'rejected', ok = l.state === 'approved', cur = l.state === 'pending';
        const c = rej ? 'var(--crit)' : ok ? 'var(--ok)' : cur ? 'var(--acc-graph)' : 'var(--line-strong)';
        return (
          <React.Fragment key={l.id ?? i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, minWidth: 88 }} data-line-kind={l.kind}>
              <div style={{ position: 'relative' }}>
                <Avatar name={l.name} userId={l.userId} size={34} tone={cur ? 'acc' : 'b'} />
                <span style={{ position: 'absolute', bottom: -3, right: -3, width: 15, height: 15, borderRadius: '50%', background: c, border: '2px solid var(--surface)', display: 'grid', placeItems: 'center' }}>
                  <Icon name={rej ? 'x' : ok ? 'check' : cur ? 'clock' : 'corner-down-right'} size={8} color="var(--n-1000)" sw={2.6} />
                </span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{l.name}</div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{l.role}</div>
                {l.standIn && <div style={{ fontSize: 10, color: 'var(--ink-3)' }} data-stand-in>대리 {l.standIn}</div>}
              </div>
              {ok || rej ? <APill tone={ok ? 'ok' : 'crit'} dot={false}>{decidedWord(l.kind, l.state)}</APill> : <StatusPill s={l.state} dot={false} />}
              {l.at && <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>{l.at}</span>}
            </div>
            {i < line.length - 1 && <div style={{ flex: '0 0 22px', display: 'flex', justifyContent: 'center', paddingTop: 12 }}><Icon name="chevron-right" size={14} color="var(--line-strong)" /></div>}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// 처리 기록의 이름표 — 행동(과 처리한 줄의 종류)대로. [이름, 의견 이름, 글자색, 바탕색]
const actionLabel = (e) => {
  if (e.action === 'submitted') return ['상신', '', 'var(--info)', 'var(--surface-2)'];
  if (e.action === 'withdrawn') return ['회수', '회수 사유', 'var(--ink-3)', 'var(--surface-2)'];
  const word = decidedWord(e.kind, e.action);
  return e.action === 'approved' ? [word, word + ' 의견', 'var(--ok)', 'var(--ok-tint)'] : [word, word + ' 의견', 'var(--crit)', 'var(--crit-tint)'];
};
// 진행 중이 아닐 때 '현재' 칸
const SETTLED = { approved: '완료', rejected: '반려', withdrawn: '회수', draft: '상신 전' };

/** 타임라인 — 처리 기록(상신·승인/합의·반려/반대·회수)과 댓글을 시간순으로. 자기 댓글은 지울 수 있다(서버가 준 canDelete) */
function Timeline({ entries, onDeleteComment }) {
  if (entries.length === 0) return null;
  return (
    <div style={{ marginTop: 18 }} data-timeline>
      <Lbl style={{ marginBottom: 8 }}>처리 이력 · 댓글</Lbl>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map(e => {
          if (e.type === 'comment') {
            return (
              <div key={'c' + e.id} data-comment={e.id} style={{ padding: '10px 12px', borderRadius: 'var(--r-ctl)', border: '1px solid var(--line)' }}>
                <div className="lbl" style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {e.name} · 댓글 <span className="mono" style={{ fontWeight: 400 }}>{e.at}</span>
                  {e.canDelete && <button className="link noprint" style={{ marginLeft: 'auto', fontSize: 11 }} onClick={() => onDeleteComment(e)}>지우기</button>}
                </div>
                <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap', color: e.deleted ? 'var(--ink-3)' : 'var(--ink)' }}>{e.deleted ? '지운 댓글입니다' : e.body}</div>
              </div>
            );
          }
          const [word, noteLabel, ink, tint] = actionLabel(e);
          return (
            <div key={'a' + e.id} data-action={e.action} style={{ padding: '10px 12px', borderRadius: 'var(--r-ctl)', background: e.comment ? tint : 'transparent', border: e.comment ? '1px solid transparent' : '1px dashed var(--line)' }}>
              <div className="lbl" style={{ color: ink, marginBottom: e.comment ? 4 : 0 }}>
                {e.name}{e.onBehalfOfName ? ` (${e.onBehalfOfName} 대리)` : ''} · {e.comment ? noteLabel : word} <span className="mono" style={{ fontWeight: 400 }}>{e.at}</span>
              </div>
              {e.comment && <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{e.comment}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 인쇄 양식 — 인쇄할 때만 보인다(화면 상세는 인쇄에서 숨김). 문서번호·양식·기안자/부서/상신일·결재란·합의란·항목 표·첨부 목록·처리 이력.
 * 브라우저 인쇄로 PDF 저장(서버 PDF 내보내기는 서버 아키텍처 §9.2 'later').
 */
function ApprovalPrint({ doc }) {
  const cell = { border: '1px solid #000', padding: '6px 8px', fontSize: 11, verticalAlign: 'top' };
  const stamps = (kind, title) => {
    const rows = doc.line.filter(l => l.kind === kind);
    if (rows.length === 0) return null;
    return (
      <table style={{ borderCollapse: 'collapse', marginLeft: 'auto', marginBottom: 8 }} data-print-stamps={kind}>
        <tbody>
          <tr><th style={{ ...cell, width: 28 }} rowSpan={3}>{title}</th>{rows.map(l => <td key={l.id} style={{ ...cell, textAlign: 'center', minWidth: 70 }}>{l.name}{l.standIn ? <div style={{ fontSize: 9 }}>대리 {l.standIn}</div> : null}</td>)}</tr>
          <tr>{rows.map(l => <td key={l.id} style={{ ...cell, textAlign: 'center', height: 34 }}>{decidedWord(l.kind, l.state) ?? (l.state === 'pending' ? '대기' : l.state === 'cancelled' ? '취소' : '')}</td>)}</tr>
          <tr>{rows.map(l => <td key={l.id} style={{ ...cell, textAlign: 'center' }}>{l.actedAt.slice(0, 10)}</td>)}</tr>
        </tbody>
      </table>
    );
  };
  return (
    <div className="printonly" data-print-doc style={{ color: '#000', background: '#fff', padding: 8 }}>
      <h1 style={{ textAlign: 'center', fontSize: 22, margin: '0 0 14px' }}>{doc.form}</h1>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
          <tbody>
            <tr><th style={cell}>문서번호</th><td style={cell}>{doc.docNo || '(상신 전)'}</td></tr>
            <tr><th style={cell}>기안자</th><td style={cell}>{doc.drafter}{doc.dept ? ' · ' + doc.dept : ''}</td></tr>
            <tr><th style={cell}>상신일</th><td style={cell}>{doc.submittedAt || '—'}</td></tr>
          </tbody>
        </table>
        <div style={{ flex: 1 }}>{stamps('approve', '결재')}{stamps('agree', '합의')}</div>
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 12 }}>
        <tbody>
          <tr><th style={{ ...cell, width: 110 }}>제목</th><td style={cell}>{doc.title}</td></tr>
          {doc.fields.map(f => <tr key={f.key}><th style={cell}>{f.label}</th><td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{f.text || '—'}</td></tr>)}
          {doc.recipients.length > 0 && <tr><th style={cell}>참조·열람</th><td style={cell}>{doc.recipients.map(r => `${r.name}(${r.role})`).join(', ')}</td></tr>}
          <tr><th style={cell}>첨부</th><td style={cell}>{(doc.files ?? []).length ? doc.files.map(f => f.original_name).join(', ') : '없음'}</td></tr>
        </tbody>
      </table>
      <div style={{ marginTop: 12, fontSize: 12, fontWeight: 700 }}>처리 이력</div>
      <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 4 }}>
        <tbody>
          {doc.timeline.filter(e => e.type === 'action').map(e => (
            <tr key={e.id}><td style={{ ...cell, width: 130 }}>{e.at}</td><td style={{ ...cell, width: 110 }}>{e.name}{e.onBehalfOfName ? ` (${e.onBehalfOfName} 대리)` : ''}</td><td style={{ ...cell, width: 60 }}>{actionLabel(e)[0]}</td><td style={cell}>{e.comment}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * 상세. 버튼은 서버가 계산한 '지금 내가 할 수 있는 행동'(actions)으로만 보인다 —
 * 결재자: 승인·반려(합의 줄이면 합의·반대 — turn.kind), 기안자: 회수(첫 결재 전), 초안 고치기·상신·지우기(임시저장), 다시 기안(반려·회수, 사용 중인 양식),
 * 볼 수 있는 사람: 댓글(상신된 문서), 자기 댓글 지우기.
 * onAction(doc, 'approve'|'reject'|'withdraw', 의견), onDraft(doc, 'edit'|'submit'|'delete'|'copy'), onComment(doc, 'add'|'delete', 내용|댓글)
 */
function ApprovalDetail({ doc, onBack, onAction, onDraft, onComment }) {
  const [comment, setComment] = uA('');
  const [reply, setReply] = uA('');
  const [confirmDelete, setConfirmDelete] = uA(false);
  const pend = doc.line.find(l => l.state === 'pending');
  const { approve, reject, withdraw, edit, submit, copy } = doc.actions;
  const remove = doc.actions.delete;
  const agree = doc.turn?.kind === 'agree';
  const okCnt = doc.line.filter(l => l.state === 'approved').length;
  const rej = doc.line.some(l => l.state === 'rejected');
  return (
    <React.Fragment>
      <div className="head">
        <button className="link" onClick={onBack}><Icon name="chevron-left" size={13} />결재함</button>
      </div>
      <div className="body">
        <div className="screenonly">
        <Panel>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>
              <APill tone="info" dot={false}>{doc.form}</APill>
              {doc.urgent && <APill tone="crit" dot>긴급</APill>}
              <StatusPill s={doc.status} />
              {doc.docNo && <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{doc.docNo}</span>}
              {/* 휴가 신청 문서: 휴가 화면에서 만들었고 결과는 휴가에 반영된다(다시 기안 없음 — 휴가 화면에서 새로 신청) */}
              {doc.subject === 'leave_request' && <a className="link noprint" href="#/leave" style={{ marginLeft: 'auto', fontSize: 12 }} data-subject-link>휴가 화면에서 보기</a>}
            </div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: '-.03em' }}>{doc.title}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
              <Avatar name={doc.drafter} userId={doc.drafterId} size={28} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{doc.drafter} <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>· {doc.dept}</span></div>
                {doc.submittedAt
                  ? <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>상신 {doc.submittedAt}</div>
                  : doc.status === 'draft' && <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>저장 {doc.updatedAt}</div>}
              </div>
            </div>
          </div>

          <div style={{ padding: '16px 20px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', display: 'flex', gap: 22, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <Lbl style={{ marginBottom: 10 }}>결재선</Lbl>
              <ApprovalLine line={doc.line} />
              {doc.recipients.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }} data-recipients>
                  {doc.recipients.map(r => (
                    <Token key={r.userId} data-recipient={r.kind} title={r.readAt ? '읽음 ' + r.readAt : undefined} tone={r.readAt ? 'ok' : 'neutral'}>
                      {r.role} · {r.name}{r.readAt ? ' · 읽음' : ''}
                    </Token>
                  ))}
                </div>
              )}
            </div>
            <div style={{ width: 172, flex: 'none', display: 'flex', flexDirection: 'column', gap: 11, paddingLeft: 20, borderLeft: '1px solid var(--line)' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span className="lbl">진행률</span>
                  <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{okCnt}<span style={{ color: 'var(--ink-3)' }}>/{doc.line.length}</span></span>
                </div>
                <span className="bar" style={{ display: 'block' }}><i style={{ width: (doc.line.length ? okCnt / doc.line.length : 0) * 100 + '%', background: rej ? 'var(--crit)' : 'var(--ok)' }} /></span>
              </div>
              {doc.fields.filter(f => f.summary).map(f => <KV key={f.key} k={f.label} v={f.text || '—'} />)}
              <KV k="현재" v={pend ? pend.name + (pend.kind === 'agree' ? ' (합의)' : '') : (SETTLED[doc.status] ?? '—')} mono={false} />
            </div>
          </div>

          <div style={{ padding: '20px' }}>
            {/* 항목은 문서가 가진 양식 사본대로 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {doc.fields.map(f => (
                <div key={f.key} data-field={f.key}>
                  <Lbl style={{ marginBottom: 4 }}>{f.label}</Lbl>
                  <div style={{ margin: 0, fontSize: 13.5, lineHeight: 1.85, whiteSpace: 'pre-wrap', color: f.text ? 'var(--ink)' : 'var(--ink-3)' }}>{f.text || '—'}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 18 }}>
              <Attachments target={{ approvalDocumentId: doc.id }} scope="approvals" />
            </div>
            <Timeline entries={doc.timeline} onDeleteComment={c => onComment(doc, 'delete', c)} />
            {doc.actions.comment && (
              <div className="noprint" style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <textarea className="inp" rows={2} value={reply} onChange={e => setReply(e.target.value)} placeholder="댓글을 쓰세요 (관계자에게 알림)"
                  style={{ flex: 1, resize: 'vertical' }} maxLength={2000} aria-label="댓글" />
                <ABtn variant="secondary" size="sm" disabled={!reply.trim()} onClick={() => reply.trim() && onComment(doc, 'add', reply.trim(), () => setReply(''))}>댓글 쓰기</ABtn>
              </div>
            )}
          </div>

          {approve || reject || withdraw ? (
            <div className="noprint" style={{ padding: '14px 20px', borderTop: '1px solid var(--line)', background: 'var(--surface-2)' }}>
              {doc.turn?.onBehalfOf && (
                <div style={{ fontSize: 12, color: 'var(--info)', marginBottom: 8 }} data-on-behalf>
                  {doc.turn.onBehalfOf.name}님의 위임으로 대신 처리합니다 — 처리 기록에 원 결재자와 함께 남습니다
                </div>
              )}
              <input className="inp" value={comment} onChange={e => setComment(e.target.value)}
                placeholder={withdraw ? '회수 사유 (선택)' : agree ? '합의 의견 (선택)' : '결재 의견 (선택)'} style={{ marginBottom: 10 }} />
              <div style={{ display: 'flex', gap: 8 }}>
                {approve && <ABtn variant="primary" size="sm" onClick={() => onAction(doc, 'approve', comment)}>{agree ? '합의' : '승인'}</ABtn>}
                {reject && <ABtn variant="danger" size="sm" onClick={() => onAction(doc, 'reject', comment)}>{agree ? '반대' : '반려'}</ABtn>}
                {withdraw && <ABtn variant="danger" size="sm" onClick={() => onAction(doc, 'withdraw', comment)}>회수</ABtn>}
                <ABtn variant="secondary" size="sm" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>인쇄</ABtn>
              </div>
            </div>
          ) : (
            <div className="noprint" style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {edit && <ABtn variant="secondary" size="sm" onClick={() => onDraft(doc, 'edit')}>고치기</ABtn>}
              {remove && <ABtn variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>지우기</ABtn>}
              {submit && <ABtn variant="primary" size="sm" onClick={() => onDraft(doc, 'submit')}>상신</ABtn>}
              {copy && <ABtn variant="primary" size="sm" onClick={() => onDraft(doc, 'copy')}>다시 기안</ABtn>}
              <ABtn variant="secondary" size="sm" style={edit || submit || copy ? { marginLeft: 12 } : undefined} onClick={() => window.print()}>인쇄</ABtn>
            </div>
          )}
        </Panel>
        </div>
        {/* 인쇄할 때만 보이는 문서 양식 — 화면 내용 뒤에 둔다(화면에서는 숨김) */}
        <ApprovalPrint doc={doc} />
      </div>
      {/* 지우기 확인은 화면 안 창으로 — 브라우저 확인 창(window.confirm)은 Teams 탭에서 뜨지 않는다 */}
      {confirmDelete && (
        <Modal width={420} title="초안 지우기" icon="file-text" onClose={() => setConfirmDelete(false)} footer={<>
          <ABtn variant="secondary" size="sm" onClick={() => setConfirmDelete(false)}>취소</ABtn>
          <ABtn variant="danger" size="sm" onClick={() => { setConfirmDelete(false); onDraft(doc, 'delete'); }}>지우기</ABtn>
        </>}>
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>«{doc.title}» 초안을 지웁니다. 지운 초안은 되살릴 수 없습니다.</div>
        </Modal>
      )}
    </React.Fragment>
  );
}

/** 양식 항목 한 칸 — 종류는 서버 양식 정의(form-schema.ts). 값 검사는 서버가 최종 */
function FieldInput({ field, value, onChange }) {
  const label = field.label + (field.required ? ' *' : '');
  const box = { padding: '9px 11px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 'var(--fs-body)', width: '100%', boxSizing: 'border-box' };
  const wrap = child => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-field={field.key}>
      <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>{label}</span>{child}
    </label>
  );
  switch (field.type) {
    case 'textarea':
      return wrap(<textarea rows={5} style={{ ...box, resize: 'vertical' }} value={value ?? ''} onChange={e => onChange(e.target.value)} />);
    case 'number':
      return wrap(<input type="number" style={box} value={value ?? ''} onChange={e => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />);
    case 'money':
      // 원 단위 정수로 저장하고 보일 때는 천 단위 쉼표
      return wrap(<input inputMode="numeric" style={box} placeholder="₩" value={value === undefined ? '' : '₩' + Number(value).toLocaleString('ko-KR')}
        onChange={e => { const d = e.target.value.replace(/[^\d]/g, ''); onChange(d ? Number(d) : undefined); }} />);
    case 'date':
      return wrap(<input type="date" style={box} value={value ?? ''} onChange={e => onChange(e.target.value || undefined)} />);
    case 'period': {
      const v = value ?? {};
      const set = (k, x) => { const next = { ...v, [k]: x || undefined }; onChange(next.start || next.end ? next : undefined); };
      return wrap(<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="date" style={box} value={v.start ?? ''} onChange={e => set('start', e.target.value)} aria-label={field.label + ' 시작'} />
        <span>~</span>
        <input type="date" style={box} value={v.end ?? ''} onChange={e => set('end', e.target.value)} aria-label={field.label + ' 끝'} />
      </span>);
    }
    case 'select':
      return wrap(<select style={box} value={value ?? ''} onChange={e => onChange(e.target.value || undefined)}>
        <option value="">— 선택하세요 —</option>
        {(field.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>);
    default:
      return wrap(<input style={box} value={value ?? ''} onChange={e => onChange(e.target.value)} />);
  }
}

/** 사람 하나 더하기 — 버튼을 누르면 고를 수 있는 재직자 목록(이미 들어간 사람·나는 빠진다) */
function AddPerson({ label, exclude, onPick }) {
  const [open, setOpen] = uA(false);
  const candidates = PEOPLE.filter(p => p.id !== ME.id && !exclude.includes(p.id));
  return (
    <span style={{ position: 'relative' }}>
      <Button size="sm" variant="ghost" onClick={() => setOpen(v => !v)}>+ {label}</Button>
      {open && (
        <div style={{ position: 'absolute', top: '120%', left: 0, zIndex: 40, width: 240, maxHeight: 250, overflowY: 'auto',
          background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-lg)', padding: 5 }}>
          {candidates.map(p => (
            <button key={p.id} onClick={() => { onPick(p); setOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '7px 9px', borderRadius: 'var(--r-sm)', textAlign: 'left' }}>
              <Avatar name={p.name} userId={p.id} size={22} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>{p.name} <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>{p.title}</span></span>
                <span style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-3)' }}>{p.dept}</span>
              </span>
            </button>
          ))}
          {candidates.length === 0 && <div style={{ padding: 12, fontSize: 12, color: 'var(--ink-3)', textAlign: 'center' }}>추가할 사람이 없습니다</div>}
        </div>
      )}
    </span>
  );
}

const STEP_LABEL = { approve: '결재', agree: '합의' };
const RECIPIENT_LABEL = { cc: '참조', view: '열람' };

/**
 * 기안 화면 — 새 기안 또는 초안 고치기(draft). 임시저장은 처음이면 초안을 만들고(그 뒤로는 이 초안을 고친다), 상신은 저장까지 한 번에.
 * 새 기안은 사용 중인 양식 중에서 고르고, 초안은 만들 때의 양식 사본(draft.schema)으로 칸을 그린다(양식은 바꿀 수 없다 — 다른 양식이면 새로 기안).
 * 결재 문서의 첨부는 저장·상신 요청의 fileIds로만 바뀐다(Attachments는 target 없이).
 */
function ApprovalCompose({ draft, onBack, onSubmitted, onToast }) {
  const [draftId, setDraftId] = uA(draft?.id ?? null);
  const [forms, setForms] = uA([]);
  const [formId, setFormId] = uA(draft?.formId ?? '');
  const [title, setTitle] = uA(draft?.title ?? '');
  const [values, setValues] = uA(draft?.values ?? {});
  // 결재선(순서 = 처리 순서, 줄마다 결재·합의)과 수신자(참조·열람)
  const [line, setLine] = uA(() => (draft?.line ?? []).map(l => ({ id: l.userId, name: l.name, kind: l.kind })));
  const [recipients, setRecipients] = uA(() => (draft?.recipients ?? []).map(r => ({ id: r.userId, name: r.name, kind: r.kind })));
  const [unresolved, setUnresolved] = uA(0);   // 기본 결재선 중 사람을 찾지 못한 줄 수
  const [attached, setAttached] = uA(draft?.files ?? []);
  const [loadError, setLoadError] = uA('');
  const [busy, setBusy] = uA(false);

  uAE(() => {
    if (draft) return;
    api.get('/api/v1/approvals/forms')
      .then(r => { setForms(r.forms); if (r.forms[0]) pick(r.forms[0]); })
      .catch(e => setLoadError(e.message));
  }, []);
  // 양식을 고르면 그 양식의 항목과(값은 비움) 기본 결재선(나를 기준으로 서버가 푼 사람)으로 채운다 — 결재·합의는 결재선, 참조·열람은 수신자
  const pick = f => {
    setFormId(f.id); setValues({});
    const person = l => ({ id: l.user.id, name: l.user.name, kind: l.kind });
    setLine(f.defaultLine.filter(l => l.user && STEP_LABEL[l.kind]).map(person));
    setRecipients(f.defaultLine.filter(l => l.user && RECIPIENT_LABEL[l.kind]).map(person));
    setUnresolved(f.defaultLine.filter(l => !l.user).length);
  };
  const form = forms.find(f => f.id === formId);
  const fields = draft ? draft.schema : (form?.fields ?? []);

  const taken = [...line, ...recipients].map(p => p.id);   // 한 문서에 같은 사람은 한 번만(결재선·참조·열람 통틀어)
  const missing = fields.filter(f => f.required && (values[f.key] === undefined || values[f.key] === ''));
  const lastIsAgree = line.length > 0 && line[line.length - 1].kind !== 'approve';
  const canSave = Boolean(draft || form) && title.trim() !== '' && !busy;
  const canSubmit = canSave && line.length > 0 && !lastIsAgree && missing.length === 0;
  const content = () => ({
    title: title.trim(), values, fileIds: attached.map(f => f.id),
    line: line.map(l => ({ userId: l.id, kind: l.kind })), recipients: recipients.map(r => ({ userId: r.id, kind: r.kind }))
  });
  const toggleKind = id => setLine(ls => ls.map(l => (l.id === id ? { ...l, kind: l.kind === 'agree' ? 'approve' : 'agree' } : l)));

  // 처음 임시저장이면 초안을 만들고, 그 뒤로는 그 초안을 고친다 — 초안 id를 돌려준다
  const saveDraft = async () => {
    if (draftId) { await api.patch('/api/v1/approvals/' + draftId, content()); return draftId; }
    const created = await api.post('/api/v1/approvals', { formId, ...content(), submit: false });
    setDraftId(created.id);
    return created.id;
  };
  const run = fn => { setBusy(true); fn().catch(e => onToast(e.message)).finally(() => setBusy(false)); };
  const save = () => run(async () => { await saveDraft(); onToast('임시저장했습니다'); });
  // 새 문서는 기안과 상신이 한 요청(첨부까지 한 트랜잭션), 초안은 고친 내용을 저장한 뒤 상신한다
  const submitNow = () => run(async () => {
    const docNo = draftId
      ? (await saveDraft().then(id => api.post('/api/v1/approvals/' + id + '/submit', {}))).docNo
      : (await api.post('/api/v1/approvals', { formId, ...content(), submit: true })).docNo;
    onSubmitted(title.trim(), docNo);
  });

  return (
    <React.Fragment>
      <div className="head">
        <button className="link" onClick={onBack}><Icon name="chevron-left" size={13} />결재함</button>
      </div>
      <div className="body">
        <Panel>
          <PH title={draftId ? '초안 고치기' : '새 기안'} sub={draftId ? '임시저장한 문서 — 상신하기 전까지 나만 봅니다' : '상신 시 결재선에 따라 순차 승인'} />
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 640 }}>
            <div>
              <Lbl style={{ marginBottom: 8 }}>문서 종류</Lbl>
              {draft ? (
                <Token tone="accent">{draft.form}</Token>
              ) : (
                <div>
                  {forms.length > 0 && <RadioGroup orientation="horizontal" aria-label="문서 종류" value={formId} disabled={Boolean(draftId)}
                    onChange={id => pick(forms.find(f => f.id === id))} options={forms.map(f => ({ value: f.id, label: f.name }))} />}
                  {forms.length === 0 && <span style={{ fontSize: 12, color: loadError ? 'var(--crit)' : 'var(--ink-3)' }}>{loadError ? '양식을 불러오지 못했습니다 — ' + loadError : '양식을 불러오는 중…'}</span>}
                </div>
              )}
              {form?.description && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 6 }}>{form.description}</div>}
            </div>
            <AField label="제목" value={title} onChange={e => setTitle(e.target.value)} placeholder="문서 제목을 입력하세요" />
            {fields.map(f => (
              <FieldInput key={formId + f.key} field={f} value={values[f.key]} onChange={v => setValues(x => ({ ...x, [f.key]: v }))} />
            ))}
            <div>
              <Lbl style={{ marginBottom: 8 }}>결재선 <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>— 순서대로 처리, 이름 옆 결재/합의를 누르면 바뀝니다</span></Lbl>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {line.map((n, i) => (
                  <span key={n.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }} data-step={n.kind}>
                    <Avatar name={n.name} userId={n.id} size={24} /><span style={{ fontSize: 12.5, fontWeight: 600 }}>{n.name}</span>
                    <SegmentedControl size="sm" aria-label={`${n.name} 결재/합의`} value={n.kind} onChange={k => { if (k !== n.kind) toggleKind(n.id); }}
                      options={[{ value: 'approve', label: STEP_LABEL.approve }, { value: 'agree', label: STEP_LABEL.agree }]} />
                    <button onClick={() => setLine(l => l.filter(x => x.id !== n.id))} aria-label="결재자 제외"
                      style={{ display: 'inline-flex', color: 'var(--ink-3)' }}><Icon name="x" size={12} /></button>
                    {i < line.length - 1 && <Icon name="chevron-right" size={13} color="var(--line-strong)" />}
                  </span>
                ))}
                <AddPerson label="결재자" exclude={taken} onPick={p => setLine(l => [...l, { id: p.id, name: p.name, kind: 'approve' }])} />
              </div>
              {lastIsAgree && <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6 }}>결재선의 마지막은 결재여야 합니다(합의로 끝날 수 없습니다)</div>}
              {unresolved > 0 && <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6 }}>양식의 기본 결재선 중 {unresolved}줄은 정해진 사람이 없어(위 부서장 없음 등) 빠졌습니다 — 필요하면 직접 더하세요</div>}
            </div>
            <div>
              <Lbl style={{ marginBottom: 8 }}>참조 · 열람 <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>— 참조는 상신할 때, 열람은 최종 승인 때 알림</span></Lbl>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {recipients.map(r => (
                  <Token key={r.id} data-recipient-edit={r.kind} onRemove={() => setRecipients(x => x.filter(y => y.id !== r.id))} removeLabel={`${r.name} 빼기`}>
                    {RECIPIENT_LABEL[r.kind]} · {r.name}
                  </Token>
                ))}
                <AddPerson label="참조" exclude={taken} onPick={p => setRecipients(x => [...x, { id: p.id, name: p.name, kind: 'cc' }])} />
                <AddPerson label="열람" exclude={taken} onPick={p => setRecipients(x => [...x, { id: p.id, name: p.name, kind: 'view' }])} />
              </div>
            </div>
            <div>
              <Attachments scope="approvals" canUpload initialFiles={draft?.files} onChange={setAttached} />
            </div>
            {missing.length > 0 && <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>필수 항목: {missing.map(f => f.label).join(', ')}</div>}
          </div>
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8, justifyContent: 'flex-end', background: 'var(--surface-2)' }}>
            <ABtn variant="secondary" size="sm" onClick={onBack}>취소</ABtn>
            <ABtn variant="secondary" size="sm" disabled={!canSave} onClick={() => canSave && save()}>임시저장</ABtn>
            <ABtn variant="primary" size="sm" disabled={!canSubmit} onClick={() => canSubmit && submitNow()}>상신</ABtn>
          </div>
        </Panel>
      </div>
    </React.Fragment>
  );
}

// 탭 = 서버 목록의 box — "내 차례"·"진행 중" 같은 판단은 결재선 상태를 아는 서버가 한다
const TABS = [
  { id: 'inbox', label: '받은 결재' }, { id: 'progress', label: '진행 중' }, { id: 'drafts', label: '임시저장' },
  { id: 'drafted', label: '올린 결재' }, { id: 'done', label: '완료' }, { id: 'rejected', label: '반려' },
  { id: 'withdrawn', label: '회수' }, { id: 'cc', label: '참조/열람' }, { id: 'all', label: '전체' }
];
const DONE_TOAST = { approve: '결재를 승인했습니다', reject: '결재를 반려했습니다', withdraw: '문서를 회수했습니다' };
const AGREE_TOAST = { approve: '합의했습니다', reject: '반대했습니다 — 결재가 끝났습니다' };
const PAGE_SIZE = 20;
const NO_FILTER = { q: '', formId: '', drafterId: '', from: '', to: '' };

/** 목록 검색 — 제목·문서번호, 양식(사용 중지 포함), 기안자, 기간(상신일 — 임시저장은 저장일). 검색을 누를 때 적용한다 */
function ApprovalSearch({ formNames, value, onApply }) {
  const [f, setF] = uA(value);
  uAE(() => { setF(value); }, [value]);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const box = { padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5 };
  const people = [...PEOPLE].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  const apply = () => onApply(f);
  return (
    <div className="noprint" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }} role="search">
      <input style={{ ...box, flex: '1 1 200px', minWidth: 160 }} placeholder="제목·문서번호" aria-label="검색어"
        value={f.q} onChange={e => set('q', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') apply(); }} />
      <select style={box} aria-label="양식" value={f.formId} onChange={e => set('formId', e.target.value)}>
        <option value="">양식 — 전체</option>
        {formNames.map(x => <option key={x.id} value={x.id}>{x.name}{x.active ? '' : ' (사용 중지)'}</option>)}
      </select>
      <select style={box} aria-label="기안자" value={f.drafterId} onChange={e => set('drafterId', e.target.value)}>
        <option value="">기안자 — 전체</option>
        {people.map(p => <option key={p.id} value={p.id}>{p.name}{p.dept ? ' · ' + p.dept : ''}</option>)}
      </select>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <input type="date" style={box} aria-label="기간 시작" value={f.from} onChange={e => set('from', e.target.value)} />
        <span style={{ color: 'var(--ink-3)' }}>~</span>
        <input type="date" style={box} aria-label="기간 끝" value={f.to} onChange={e => set('to', e.target.value)} />
      </span>
      <ABtn variant="secondary" size="sm" onClick={apply}>검색</ABtn>
      <ABtn variant="secondary" size="sm" onClick={() => { setF(NO_FILTER); onApply(NO_FILTER); }}>초기화</ABtn>
    </div>
  );
}

/** 쪽 이동 — 전체 건수는 서버가 센 값 */
function Pager({ page, total, pageSize, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  const from = Math.max(1, Math.min(page - 2, pages - 4)), to = Math.min(pages, from + 4);
  const nums = Array.from({ length: to - from + 1 }, (_, i) => from + i);
  return (
    <nav aria-label="쪽" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="이전 쪽">‹</Button>
      {nums.map(n => (
        <Button key={n} size="sm" variant={n === page ? 'secondary' : 'ghost'} aria-current={n === page ? 'page' : undefined} onClick={() => onPage(n)}>{n}</Button>
      ))}
      <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="다음 쪽">›</Button>
    </nav>
  );
}

/** onChanged: 결재를 처리·상신한 뒤 셸이 배지·홈 값을 서버에서 다시 받게 한다 */
function Approval({ deepDoc, onToast, onChanged }) {
  const [mode, setMode] = uA('list');       // list | detail | compose
  const [tab, setTab] = uA('inbox');
  const [sel, setSel] = uA(null);
  const [editing, setEditing] = uA(null);   // 고칠 초안(없으면 새 기안)
  const [filter, setFilter] = uA(NO_FILTER);
  const [page, setPage] = uA(1);
  const [list, setList] = uA({ documents: [], total: 0 });
  const [rev, setRev] = uA(0);              // 같은 탭·쪽이어도 처리 뒤에는 다시 받는다
  const [formNames, setFormNames] = uA([]);

  // 양식 관리 탭은 서버가 관리 권한을 준 사람에게만(목록 API의 actions.manage)
  const [canManageForms, setCanManageForms] = uA(false);
  uAE(() => { api.get('/api/v1/approvals/forms').then(r => setCanManageForms(r.actions?.manage === true)).catch(() => setCanManageForms(false)); }, []);
  uAE(() => { api.get('/api/v1/approvals/forms/names').then(r => setFormNames(r.forms)).catch(e => onToast(e.message)); }, [onToast]);

  // 탭·쪽·검색 조건이 바뀌거나 처리한 뒤(rev) 다시 받는다. 앞선 요청의 늦은 응답은 버린다(탭을 빨리 바꿔도 지금 조건의 목록만 보이게)
  uAE(() => {
    if (tab === 'forms' || tab === 'delegations') return undefined;
    let current = true;
    const q = new URLSearchParams({ box: tab, page: String(page), pageSize: String(PAGE_SIZE) });
    for (const [k, v] of Object.entries(filter)) if (v.trim()) q.set(k, v.trim());
    api.get('/api/v1/approvals?' + q.toString())
      .then(r => { if (current) setList({ documents: mapApprovals(r.documents), total: r.total }); })
      .catch(e => { if (current) onToast(e.message); });
    return () => { current = false; };
  }, [tab, page, filter, rev, onToast]);

  // 상세는 결재선·이력·댓글까지 필요하므로 열 때 개별 조회한다(첨부 이름은 인쇄 양식에도 쓴다)
  // 상세가 거절되면(열람 규칙) 그 이유를 알린다 — 첨부 목록은 같은 규칙이라 함께 거절되지만 상세의 이유가 먼저다
  const openDoc = React.useCallback(doc =>
    Promise.allSettled([api.get('/api/v1/approvals/' + doc.id), api.get('/api/v1/files?approvalDocumentId=' + doc.id)])
      .then(([r, f]) => {
        if (r.status === 'rejected') { onToast(r.reason.message); return; }
        if (f.status === 'rejected') onToast('첨부 목록을 받지 못했습니다 — ' + f.reason.message);
        setSel({ ...mapApprovalDetail(r.value), files: f.status === 'fulfilled' ? f.value.files : [] }); setMode('detail');
      }), [onToast]);
  // 깊은 링크: 문서 id면 상세, 'delegations'면 위임 탭(위임 알림의 링크 — 계획 문서 5.2 2-1d)
  uAE(() => {
    if (!deepDoc) return;
    if (deepDoc.id === 'delegations') { setTab('delegations'); setMode('list'); return; }
    void openDoc(deepDoc);
  }, [deepDoc, openDoc]);

  const showTab = id => { setTab(id); setPage(1); setMode('list'); setEditing(null); setRev(r => r + 1); };
  const backToList = () => { setMode('list'); setEditing(null); setRev(r => r + 1); };

  const act = (doc, action, comment) => {
    api.post('/api/v1/approvals/' + doc.id + '/' + action, { comment })
      .then(() => { onToast((doc.turn?.kind === 'agree' && AGREE_TOAST[action]) || DONE_TOAST[action]); onChanged?.(); backToList(); })
      .catch(e => onToast(e.message));
  };

  // 댓글 쓰기·지우기 — 끝나면 상세를 다시 받는다(타임라인은 서버 값)
  const commentAction = (doc, what, arg, done) => {
    const call = what === 'add'
      ? api.post('/api/v1/approvals/' + doc.id + '/comments', { body: arg })
      : api.del('/api/v1/approvals/' + doc.id + '/comments/' + arg.id);
    call.then(() => { done?.(); onToast(what === 'add' ? '댓글을 썼습니다' : '댓글을 지웠습니다'); return openDoc(doc); })
      .catch(e => onToast(e.message));
  };

  // 초안 고치기 화면은 상세(양식 사본·값·결재선·수신자)와 지금 붙어 있는 첨부로 채운다
  const editDraft = id => Promise.all([api.get('/api/v1/approvals/' + id), api.get('/api/v1/files?approvalDocumentId=' + id)])
    .then(([d, f]) => { setEditing({ ...mapApprovalDetail(d), files: f.files }); setMode('compose'); });
  const draftAction = (doc, action) => {
    const done = {
      edit: () => editDraft(doc.id),
      submit: () => api.post('/api/v1/approvals/' + doc.id + '/submit', {}).then(r => {
        onToast('«' + doc.title + '» 상신 완료 · ' + r.docNo); onChanged?.(); showTab('drafted');
      }),
      delete: () => api.del('/api/v1/approvals/' + doc.id).then(() => { onToast('초안을 지웠습니다'); showTab('drafts'); }),
      // 다시 기안: 같은 내용으로 새 초안을 만들고 바로 고치기 화면으로. 지금 양식에 맞지 않아 옮기지 못한 값·재직이 끝난 사람은 알린다
      copy: () => api.post('/api/v1/approvals/' + doc.id + '/copy', {}).then(r => {
        const lost = [
          r.droppedFields.length ? '옮기지 못한 항목: ' + r.droppedFields.join(', ') : '',
          r.droppedPeople.length ? '재직 중이 아니라 뺀 사람: ' + r.droppedPeople.join(', ') : ''
        ].filter(Boolean).join(' · ');
        onToast('새 초안을 만들었습니다' + (lost ? ' — ' + lost : ''));
        return editDraft(r.id);
      })
    }[action];
    done().catch(e => onToast(e.message));
  };

  if (mode === 'detail' && sel) {
    return <ApprovalDetail key={sel.id} doc={sel} onBack={backToList} onAction={act} onDraft={draftAction} onComment={commentAction} />;
  }
  if (mode === 'compose') {
    return <ApprovalCompose key={editing?.id ?? 'new'} draft={editing} onToast={onToast}
      onBack={backToList}
      onSubmitted={(title, docNo) => { onToast('«' + title + '» 상신 완료 · ' + docNo); setEditing(null); onChanged?.(); showTab('drafted'); }} />;
  }

  const docs = list.documents;
  const tabs = [...TABS, { id: 'delegations', label: '위임' }, ...(canManageForms ? [{ id: 'forms', label: '양식 관리' }] : [])];
  const listTab = tab !== 'forms' && tab !== 'delegations';
  return (
    <React.Fragment>
      <div className="head">
        <h1>전자결재</h1>
        <div className="meta"><APill tone="neutral" dot={false}><span className="mono">{list.total} DOCS</span></APill></div>
        <div className="r"><ABtn variant="primary" size="sm" onClick={() => { setEditing(null); setMode('compose'); }}>기안 작성</ABtn></div>
      </div>
      <PageTabs value={tab} onChange={showTab} items={tabs.map(t => ({ value: t.id, label: t.label, count: tab === t.id && listTab ? list.total : undefined }))} />
      <div className="body" style={{ paddingTop: 14 }}>
        {tab === 'forms' ? <FormManager onToast={onToast} /> : tab === 'delegations' ? <DelegationManager onToast={onToast} /> : <React.Fragment>
          <ApprovalSearch formNames={formNames} value={filter} onApply={f => { setFilter(f); setPage(1); }} />
          <Panel scroll>
            {docs.length === 0
              ? <Empty title="해당 항목이 없습니다" sub={Object.values(filter).some(v => v) ? '검색 조건을 바꿔 보세요' : '새 문서가 도착하면 알림으로 안내됩니다'} />
              : <ADT
                  columns={[
                    { key: 'title', header: '문서', strong: true, render: d => (
                      <button onClick={() => openDoc(d)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0, textAlign: 'left', fontWeight: 600, color: 'var(--ink)' }}>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</span>
                        {d.urgent && <APill tone="crit" dot={false}>긴급</APill>}
                      </button>) },
                    { key: 'docNo', header: '문서번호', width: 112, render: d => <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{d.docNo || '—'}</span> },
                    { key: 'form', header: '종류', width: 96, render: d => <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{d.form}</span> },
                    { key: 'drafter', header: '기안자', width: 104, render: d => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}><Avatar name={d.drafter} userId={d.drafterId} size={20} />{d.drafter}</span> },
                    { key: 'amount', header: '금액 / 기간', align: 'right', width: 122, mono: true },
                    { key: 'cur', header: '현재', width: 92, render: d => (
                      <span style={{ fontSize: 12, color: d.myTurn ? 'var(--acc)' : 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                        {d.myTurn ? (d.turnFor ? `대리 차례(${d.turnFor})` : d.currentKind === 'agree' ? '내 합의 차례' : '내 차례') : d.current ? d.current + (d.currentKind === 'agree' ? ' 합의중' : ' 결재중') : '—'}</span>) },
                    // 상신한 문서는 상신 시각, 임시저장은 마지막 저장 시각(서버 정렬·기간 검색의 기준과 같다)
                    { key: 'at', header: tab === 'drafts' ? '저장' : '상신', align: 'right', width: 66,
                      render: d => <span className="mono">{(d.submittedAt || d.updatedAt).slice(5, 10)}</span> },
                    { key: 'status', header: '상태', width: 78, render: d => <StatusPill s={d.status} /> },
                  ]}
                  rows={docs}
                  footer={<React.Fragment>
                    <span>총 {list.total}건</span>
                    <Pager page={page} total={list.total} pageSize={PAGE_SIZE} onPage={setPage} />
                    <span style={{ marginLeft: 'auto' }}>문서명을 클릭하면 상세</span>
                  </React.Fragment>} />}
          </Panel>
        </React.Fragment>}
      </div>
    </React.Fragment>
  );
}

export { Approval, ApprovalCompose, ApprovalDetail, ApprovalLine };
